import React, { useRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import enTexts from './NotFoundEn.json';
import deTexts from './NotFoundDe.json';
import styles from './NotFound.module.css';

// Constants for canvas and game objects
const CANVAS_WIDTH = 800;
const CANVAS_HEIGHT = 600;
const SHIP_WIDTH = 60;
const SHIP_HEIGHT = 60;
const OBSTACLE_WIDTH = 80;
const OBSTACLE_HEIGHT = 80;
const OBSTACLE_SPEED = 3;

function NotFound() {
    // Internationalization setup using i18next
    const { i18n } = useTranslation();
    const currentLang = i18n.language;
    const texts = currentLang.startsWith('de') ? deTexts : enTexts;

    // Ref for the canvas element
    const canvasRef = useRef(null);
    // Using refs to store frequently updated game data to avoid unnecessary re-renders.
    const obstaclesRef = useRef([]); // Array to hold obstacle objects
    const shipYRef = useRef(CANVAS_HEIGHT / 2 - SHIP_HEIGHT / 2); // Vertical position of the ship

    // State to control game over status (used for showing UI elements like the restart button)
    const [gameOver, setGameOver] = useState(false);
    // State to track the score (number of obstacles that have passed off-screen)
    const [score, setScore] = useState(0);
    // A dummy state update function to force a re-render if needed (e.g., after a key press)
    const [, forceUpdate] = useState(0);

    // Effect to add new obstacles at regular intervals (every 1.5 seconds)
    useEffect(() => {
        const interval = setInterval(() => {
            // Push a new obstacle starting from the right edge with a random vertical position
            obstaclesRef.current.push({
                x: CANVAS_WIDTH,
                y: Math.random() * (CANVAS_HEIGHT - OBSTACLE_HEIGHT),
            });
        }, 1500);

        // Clear the interval when the component unmounts
        return () => clearInterval(interval);
    }, []);

    // Main game loop using requestAnimationFrame
    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        let animationFrameId;

        // Retrieve CSS variables for colors
        const rootStyles = getComputedStyle(document.documentElement);
        const shipColor = rootStyles.getPropertyValue('--primary-color').trim() || 'blue';
        const obstacleColor = rootStyles.getPropertyValue('--accent-color').trim() || 'blue';
        const gameOverColor = rootStyles.getPropertyValue('--headline-color').trim() || 'blue';

        // Update function: moves obstacles, removes off-screen obstacles (increasing score), and checks for collisions
        const update = () => {
            // Store current number of obstacles for score calculation
            const currentCount = obstaclesRef.current.length;
            // Move each obstacle to the left
            const updatedObstacles = obstaclesRef.current
                .map(obs => ({ ...obs, x: obs.x - OBSTACLE_SPEED }))
                .filter(obs => obs.x + OBSTACLE_WIDTH > 0); // Remove obstacles that have moved off-screen

            // Calculate how many obstacles were removed (i.e. that have passed off-screen)
            const removedCount = currentCount - updatedObstacles.length;
            if (removedCount > 0) {
                setScore(prevScore => prevScore + removedCount);
            }

            obstaclesRef.current = updatedObstacles;

            // Collision detection between the ship (at the left) and each obstacle
            obstaclesRef.current.forEach(obs => {
                if (
                    // Check if the right side of the ship is to the right of the left side of the obstacle
                    0 < obs.x + OBSTACLE_WIDTH &&
                    // Check if the left side of the ship is to the left of the right side of the obstacle
                    SHIP_WIDTH > obs.x &&
                    // Check if the top of the ship is above the bottom of the obstacle
                    shipYRef.current < obs.y + OBSTACLE_HEIGHT &&
                    // Check if the bottom of the ship is below the top of the obstacle
                    shipYRef.current + SHIP_HEIGHT > obs.y
                ) {
                    // Collision detected: set game over state to true
                    setGameOver(true);
                }
            });
        };

        // Draw function: clears the canvas and draws the ship, obstacles, and game over text if needed
        const draw = () => {
            // Clear canvas
            ctx.clearRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

            // Draw the ship using the CSS variable color
            ctx.fillStyle = shipColor;
            ctx.fillRect(0, shipYRef.current, SHIP_WIDTH, SHIP_HEIGHT);

            // Draw obstacles using the CSS variable color
            ctx.fillStyle = obstacleColor;
            obstaclesRef.current.forEach(obs => {
                ctx.fillRect(obs.x, obs.y, OBSTACLE_WIDTH, OBSTACLE_HEIGHT);
            });

            // Display "Game Over" message if needed
            if (gameOver) {
                ctx.fillStyle = gameOverColor;
                ctx.font = '48px sans-serif';
                ctx.fillText('Game Over', CANVAS_WIDTH / 2 - 100, CANVAS_HEIGHT / 2);
            }
        };

        // The game loop function that updates game logic and redraws each frame
        const gameLoop = () => {
            if (!gameOver) {
                update();
            }
            draw();
            // Schedule the next frame
            animationFrameId = requestAnimationFrame(gameLoop);
        };

        // Start the game loop
        gameLoop();

        // Cleanup function to cancel the animation frame when component unmounts or gameOver changes
        return () => cancelAnimationFrame(animationFrameId);
    }, [gameOver]);

    // Effect to handle keyboard input for moving the ship up and down
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (gameOver) return; // Do nothing if the game is over

            // Check if the up or down arrow key is pressed
            if (e.key === 'ArrowUp') {
                // Move the ship up but not above the canvas
                shipYRef.current = Math.max(shipYRef.current - 20, 0);
                // Force a re-render if UI elements depend on ship position (optional)
                forceUpdate(n => n + 1);
            } else if (e.key === 'ArrowDown') {
                // Move the ship down but not below the canvas
                shipYRef.current = Math.min(shipYRef.current + 20, CANVAS_HEIGHT - SHIP_HEIGHT);
                forceUpdate(n => n + 1);
            }
        };

        // Add keydown event listener
        window.addEventListener('keydown', handleKeyDown);
        // Remove event listener on cleanup
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [gameOver]);

    // Render the internationalized text, score, and the canvas.
    // When game over, a restart button is displayed that reloads the page.
    return (
        <div className={styles.container}>
            {/* Display the title and text based on the current language */}
            <h1>{texts.title}</h1>
            <p>{texts.text}</p>

            {/* Display the current score */}
            <div className={styles.score}>Score: {score}</div>

            {/* The canvas element where the game is rendered */}
            <canvas
                ref={canvasRef}
                width={CANVAS_WIDTH}
                height={CANVAS_HEIGHT}
                style={{ border: '1px solid var(--border-color)' }}
            />

            {/* Display a restart button if the game is over */}
            <div>
                {gameOver && (
                    <button className={styles.restartButton} onClick={() => window.location.reload()}>
                        {texts.restart}
                    </button>
                )}
            </div>
        </div>
    );
}

export default NotFound;
